var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef, useMemo, useRef, useLayoutEffect } from 'react';
import MathEditor from '../components/MathEditor';
var WHITESPACE_CHAR = '​';
// Regex patterns for normalising simple math items to plain text
var SIMPLE_CHAR_RE = /^[a-zA-Z0-9]$/;
var TEXT_MODE_RE = /^\\text\{([a-zA-Z0-9])\}$/;
var FractionTileMathEditor = forwardRef(function (props, ref) {
    var _a, _b;
    var viewonly = props.viewonly;
    var containerRef = useRef(null);
    var _c = React.useState(true), isEmpty = _c[0], setIsEmpty = _c[1];
    // Normalise the text data before it reaches MathEditor:
    //  • Single-letter/digit math items (e.g. {type:'math', latex:'a'}) are
    //    converted to text items so they render in the same font as surrounding
    //    plain text (avoiding math-italic vs. upright size inconsistencies).
    //  • Consecutive text items are merged into one so MathEditor creates a
    //    single text node, guaranteeing all characters in the same anonymous
    //    flex item and consistent browser font rendering.
    var normalizedData = useMemo(function () {
        var _a, _b;
        var rawText = (_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.text;
        if (!rawText || !Array.isArray(rawText) || rawText.length === 0)
            return props.data;
        var changed = false;
        var merged = [];
        for (var _i = 0, rawText_1 = rawText; _i < rawText_1.length; _i++) {
            var item = rawText_1[_i];
            if (item.type === 'math') {
                var latex = item.latex || '';
                var textModeMatch = TEXT_MODE_RE.exec(latex);
                var char = SIMPLE_CHAR_RE.test(latex)
                    ? latex
                    : textModeMatch
                        ? textModeMatch[1]
                        : null;
                if (char !== null) {
                    changed = true;
                    var last = merged[merged.length - 1];
                    if ((last === null || last === void 0 ? void 0 : last.type) === 'text') {
                        merged[merged.length - 1] = __assign(__assign({}, last), { value: last.value + char });
                    }
                    else {
                        merged.push({ type: 'text', value: char });
                    }
                    continue;
                }
            }
            else if (item.type === 'text') {
                var last = merged[merged.length - 1];
                if ((last === null || last === void 0 ? void 0 : last.type) === 'text') {
                    changed = true;
                    merged[merged.length - 1] = __assign(__assign({}, last), { value: last.value + item.value });
                    continue;
                }
            }
            merged.push(item);
        }
        if (!changed)
            return props.data;
        return __assign(__assign({}, props.data), { data: __assign(__assign({}, props.data.data), { text: merged }) });
    }, [props.data]);
    var checkIsEmpty = function (editor) {
        var text = editor.textContent || '';
        var hasContent = text.replace(new RegExp("".concat(WHITESPACE_CHAR), 'g'), '').length > 0 ||
            editor.querySelector('math-field');
        return !hasContent;
    };
    useLayoutEffect(function () {
        var wrapper = containerRef.current;
        if (!wrapper)
            return;
        var editor = wrapper.querySelector('.editorDiv');
        if (!editor)
            return;
        var setupEditor = function () {
            var empty = checkIsEmpty(editor);
            setIsEmpty(empty);
            if (empty) {
                // Do NOT inject WHITESPACE_CHAR (​).  The zero-width space sits
                // before the user's text and causes the font to apply different
                // contextual shaping to the first typed character (e.g. the 'e' after
                // ​ vs the 'e' after another 'e'), making consecutive identical
                // letters like "ee" or "aa" render at inconsistent visual sizes.
                // Modern contenteditable handles cursor placement in an empty div
                // without needing a placeholder text node.
                if (editor.innerHTML === WHITESPACE_CHAR || editor.innerHTML === '<br>') {
                    editor.innerHTML = '';
                }
                editor.classList.add('is-empty');
            }
            else {
                editor.classList.remove('is-empty');
            }
            if (props.placeHolder) {
                editor.setAttribute('data-placeholder', props.placeHolder);
            }
        };
        setupEditor();
        var observer = new MutationObserver(setupEditor);
        observer.observe(editor, {
            childList: true,
            subtree: true,
            characterData: true,
        });
        return function () { return observer.disconnect(); };
    }, [(_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.text, props.placeHolder]);
    return (_jsx("div", { ref: containerRef, style: { display: 'contents', pointerEvents: viewonly ? 'none' : undefined }, children: _jsx(MathEditor, __assign({ ref: ref }, props, { data: normalizedData, viewonly: viewonly, keepSelectedOnInternalBlur: true, stickyKeypadAfterInsertOnTouch: true, className: "".concat(props.className || '', " ").concat(isEmpty ? 'is-empty' : ''), placeHolder: "" })) }));
});
export default FractionTileMathEditor;
